import React, { useCallback, useMemo, useState } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';

import { TreeNav } from 'ui-components/components/tree-navigation';

import DataDrivenLogo from '../../../components/data-driven-logo';
import { SearchForm } from '../../../components/shipping-grid/search-form';
import {
  RETURNS_GRID_SIDEBAR_CLEAR_QUICK_SEARCH,
  RETURNS_GRID_SIDEBAR_QUICK_SEARCH,
  RETURNS_GRID_SIDEBAR_SELECT_STORE,
} from '../../../constants/analytics-events';
import { BASE_RETURNS_PATH } from '../../../routes/constants';
import { Segment } from '../../../services/analytics-services/segment';
import { QUICK_SEARCH_LOCATION_KEY } from '../../../services/quicksearch-location-service';
import Global from '../../../store/global-selectors';
import { returnsSearch } from '../../../store/returns/nav/returns.nav.actions';
import { getReturns } from '../../../store/returns/returns.actions';
import {
  ALL_STORES_STORE_GUID,
  NAVITEM_STANDARD_RETURNS_ID,
  STATUS_OUTSTANDING,
  STATUS_RECEIVED,
  STATUS_VOIDED,
  STATUS_AWAITING_SHIPMENT,
  PATH_ENHANCED_RETURNS,
  resetPageQueryParam,
} from '../../../store/returns/returns.helpers';
import { useEnhancedReturnsCountContext } from '../enhanced-returns-count-context';
import { useIsShipstationReturnsEnabled } from '../../../hooks/use-seller-setting';
import styles from './ssr-left-nav.module.less';

const returnsMessage = defineMessage({
  description: 'Accordion label for the returns section in the returns navigation',
  defaultMessage: 'Returns',
  id: '4Tmp9v',
});

const legacyReturnsMessage = defineMessage({
  description: 'Left navigation item that when clicked, shows the legacy returns grid',
  defaultMessage: 'Legacy Returns',
  id: 'Bgp3Y6',
});

const ALL_STORES_ID = ALL_STORES_STORE_GUID;

export const SSRLeftNav = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const stores = useSelector(Global.selectSellerActiveStores);
  const enhancedReturnsMatch = useRouteMatch(`${PATH_ENHANCED_RETURNS}/:storeGuid?`);
  const selectedStoreGuid = enhancedReturnsMatch?.params.storeGuid;

  const standardReturnsMatch = useRouteMatch(`${BASE_RETURNS_PATH}/${NAVITEM_STANDARD_RETURNS_ID}/:statusId?`);
  const selectedStandardReturnsStatus = standardReturnsMatch?.params.statusId;

  const standardReturnsSearchCount = useSelector(Global.selectReturnsNavSearchTotalCount);
  const [returnSearchInputValue, setReturnSearchInputValue] = useState('');
  const [isReturnsExpanded, setIsReturnsExpanded] = useState(true);
  const standardReturnsPage = useSelector(Global.selectReturnsGridPage);
  const standardReturnsPageSize = useSelector(Global.selectReturnsGridPageSize);

  const buildTagPropsFunc = useMemo(() => {
    const newSearch = resetPageQueryParam(history.location.search);
    const quickSearch = new URLSearchParams(history.location.search).get(QUICK_SEARCH_LOCATION_KEY);
    const standardReturnsParams = new URLSearchParams(`page=${standardReturnsPage}&pageSize=${standardReturnsPageSize}`);
    if (quickSearch) {
      standardReturnsParams.set(QUICK_SEARCH_LOCATION_KEY, quickSearch);
    }

    return (item) => {
      if (item.id === NAVITEM_STANDARD_RETURNS_ID) {
        return {
          to: {
            pathname: `${BASE_RETURNS_PATH}/${NAVITEM_STANDARD_RETURNS_ID}/${STATUS_OUTSTANDING}`,
            search: standardReturnsParams.toString(),
          },
        };
      }

      return {
        to: {
          pathname: `${PATH_ENHANCED_RETURNS}/${item.id}/${STATUS_AWAITING_SHIPMENT}`,
          search: newSearch,
        },
      };
    };
  }, [standardReturnsPage, standardReturnsPageSize, history.location.search]);

  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const { onSearch: onEnhancedReturnsSearch, countsPerStoreMap, isSearchMode, onClearSearchMode } = useEnhancedReturnsCountContext();

  const onSearch = useCallback(
    (searchData) => {
      Segment.trackEvent(RETURNS_GRID_SIDEBAR_QUICK_SEARCH, {
        type: RETURNS_GRID_SIDEBAR_QUICK_SEARCH,
        searchData,
      });

      dispatch(returnsSearch(searchData));
      onEnhancedReturnsSearch(searchData.searchTerm);
    },
    [dispatch, onEnhancedReturnsSearch],
  );

  const onClearSearchInput = useCallback(() => {
    Segment.trackEvent(RETURNS_GRID_SIDEBAR_CLEAR_QUICK_SEARCH, {
      type: RETURNS_GRID_SIDEBAR_CLEAR_QUICK_SEARCH,
    });

    setReturnSearchInputValue('');
    onClearSearchMode();
  }, [onClearSearchMode]);

  const returnsSearchFormProps = useMemo(
    () => ({
      clearSearchInput: onClearSearchInput,
      searchInputValue: returnSearchInputValue,
      handleSearchInputChange: setReturnSearchInputValue,
      isHighlighted: isSearchMode,
      onSearch,
      resetGrid: () => dispatch(getReturns()),
    }),
    [onClearSearchInput, returnSearchInputValue, isSearchMode, onSearch, dispatch],
  );

  const navItems = useMemo(() => {
    const items = [];

    if (stores.length) {
      const storeItems = stores.map((store) => {
        const totalCount = countsPerStoreMap[store.storeGuid]?.totalCount || 0;
        return {
          id: store.storeGuid,
          label: store.name,
          selected: selectedStoreGuid === store.storeGuid,
          count: totalCount,
          shouldRenderAnIcon: true,
          customIcon: <DataDrivenLogo logoType='icon' marketplaceId={store.marketplaceId} />,
        };
      });
      items.push({
        id: ALL_STORES_ID,
        label: <FormattedMessage {...returnsMessage} />,
        selected: selectedStoreGuid === ALL_STORES_ID,
        expanded: isReturnsExpanded,
        items: storeItems,
      });
    }

    if (!isShipstationReturnsEnabled) {
      items.push({
        id: NAVITEM_STANDARD_RETURNS_ID,
        label: <FormattedMessage {...legacyReturnsMessage} />,
        selected: [STATUS_OUTSTANDING, STATUS_RECEIVED, STATUS_VOIDED].includes(selectedStandardReturnsStatus),
        count: isSearchMode ? standardReturnsSearchCount : undefined,
      });
    }

    return items;
  }, [
    countsPerStoreMap,
    isShipstationReturnsEnabled,
    isReturnsExpanded,
    isSearchMode,
    selectedStandardReturnsStatus,
    selectedStoreGuid,
    standardReturnsSearchCount,
    stores,
  ]);

  const onItemSelected = useCallback((item) => {
    Segment.trackEvent(RETURNS_GRID_SIDEBAR_SELECT_STORE, {
      type: RETURNS_GRID_SIDEBAR_SELECT_STORE,
      item,
    });
  }, []);

  const onExpandClick = useCallback((item) => {
    if (item.id === ALL_STORES_ID) {
      setIsReturnsExpanded((prev) => !prev);
    }
  }, []);

  return (
    <div className={styles['nav-wrapper']}>
      <SearchForm {...returnsSearchFormProps} isReturnsPage />
      <div className={styles['tree-nav-container']}>
        <TreeNav
          buildTagProps={buildTagPropsFunc}
          isHighlighted={isSearchMode}
          items={navItems}
          onExpandClick={onExpandClick}
          onItemSelected={onItemSelected}
        />
      </div>
    </div>
  );
};
